import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Product2, ProductName, RenderedOutputSummary } from 'situview-webapi-client';
import { ConfigService } from 'src/app/services/config.service';
import { SituReel, SituReelItem, SituReelService } from 'src/app/services/situ-reel.service';
import { SituReelItemPickerDialogComponent } from '../situ-reel-item-picker-dialog/situ-reel-item-picker-dialog.component';
import { SituReelProductPickerDialogComponent } from '../situ-reel-product-picker-dialog/situ-reel-product-picker-dialog.component';
import {
  SituReelStartViewPickerDialogComponent,
  SituReelStartViewPickerData,
  SituReelStartViewPickerResult
} from '../situ-reel-start-view-picker-dialog/situ-reel-start-view-picker-dialog.component';

export interface SituReelEditDialogData {
  reel: SituReel | null;
  companyId: string;
}

interface ItemFormValue {
  artifactId: string;
  thumbnailArtifactId?: string;
  label: string;
  subtitle?: string;
  productName?: ProductName;
  thumbnailOverrideUrl?: string;
  startViewQuery?: string;
  displayOrder: number;
}

const SITUVIEW_PRODUCT_NAMES = new Set<ProductName>([
  ProductName.Panorama,
  ProductName.RayTracedDraft,
  ProductName.RayTraced,
  ProductName.RayTracedPlus
]);

@Component({
  selector: 'situ-reel-edit-dialog',
  templateUrl: './situ-reel-edit-dialog.component.html',
  styleUrls: ['./situ-reel-edit-dialog.component.scss']
})
export class SituReelEditDialogComponent implements OnInit {
  form!: FormGroup;
  reelId: string;
  defaultArtifactId: string | undefined;
  saving = false;
  expandedItemIndex: number | null = null;
  thumbnailFailed: { [artifactId: string]: boolean } = {};

  readonly productLabels: { [key in ProductName]?: string } = {
    [ProductName.Panorama]: 'situVIEW',
    [ProductName.RayTracedDraft]: 'situVIEW Draft',
    [ProductName.RayTraced]: 'situVIEW HD',
    [ProductName.RayTracedPlus]: 'situVIEW HD+',
    [ProductName.Facade]: 'situFACADE',
    [ProductName.Plan]: 'situPLAN',
    [ProductName.Render]: 'situRENDER',
    [ProductName.ProductPreview]: 'situPRODUCT',
    [ProductName.Unreal]: 'situHOME'
  };

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: SituReelEditDialogData,
    private fb: FormBuilder,
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<SituReelEditDialogComponent, SituReel>,
    private snackBar: MatSnackBar,
    private configService: ConfigService,
    private reelService: SituReelService) {
    this.reelId = data?.reel?.id ?? '';
  }

  ngOnInit(): void {
    const reel = this.data?.reel;
    this.form = this.fb.group({
      name: [reel?.name ?? '', [Validators.required, Validators.maxLength(120)]],
      description: [reel?.description ?? '', [Validators.maxLength(280)]],
      items: this.fb.array(
        (reel?.items ?? [])
          .slice()
          .sort((a, b) => a.displayOrder - b.displayOrder)
          .map((item, idx) => this.makeItemGroup(item, idx))
      )
    });
    this.defaultArtifactId = reel?.defaultArtifactId;
  }

  get items(): FormArray {
    return this.form.get('items') as FormArray;
  }

  itemControl(index: number): FormGroup {
    return this.items.at(index) as FormGroup;
  }

  isDefault(index: number): boolean {
    return this.items.at(index).get('artifactId')?.value === this.defaultArtifactId;
  }

  setDefault(index: number): void {
    this.defaultArtifactId = this.items.at(index).get('artifactId')?.value;
  }

  moveUp(index: number): void {
    if (index <= 0) return;
    this.swap(index, index - 1);
  }

  moveDown(index: number): void {
    if (index >= this.items.length - 1) return;
    this.swap(index, index + 1);
  }

  removeItem(index: number): void {
    const removed = this.items.at(index).value as ItemFormValue;
    this.items.removeAt(index);
    this.renumber();
    if (removed.artifactId === this.defaultArtifactId) {
      this.defaultArtifactId = this.items.length > 0
        ? this.items.at(0).get('artifactId')?.value
        : undefined;
    }
    if (this.expandedItemIndex !== null && this.expandedItemIndex >= this.items.length) {
      this.expandedItemIndex = null;
    }
  }

  toggleExpand(index: number): void {
    this.expandedItemIndex = this.expandedItemIndex === index ? null : index;
  }

  clearOverride(index: number): void {
    this.items.at(index).get('thumbnailOverrideUrl')?.setValue('');
  }

  hasOverride(index: number): boolean {
    return !!this.items.at(index).get('thumbnailOverrideUrl')?.value;
  }

  supportsStartView(ctrl: AbstractControl): boolean {
    const productName = (ctrl.value as ItemFormValue)?.productName;
    return productName !== undefined && productName !== null && SITUVIEW_PRODUCT_NAMES.has(productName);
  }

  hasStartView(ctrl: AbstractControl): boolean {
    return !!(ctrl.value as ItemFormValue)?.startViewQuery;
  }

  describeStartView(ctrl: AbstractControl): string {
    const query = (ctrl.value as ItemFormValue)?.startViewQuery;
    if (!query) return 'No starting view set';
    try {
      const sp = new URLSearchParams(query);
      const panorama = sp.get('panorama');
      const h = sp.get('hlookat');
      const v = sp.get('vlookat');
      if (!panorama) return 'Starting view set';
      const angle = h !== null && v !== null ? ` · ${h}°/${v}°`
        : h !== null ? ` · ${h}°`
        : '';
      return `Opens at ${panorama}${angle}`;
    } catch {
      return 'Starting view set';
    }
  }

  openStartViewPicker(index: number): void {
    const ctrl = this.itemControl(index);
    const value = ctrl.value as ItemFormValue;
    const ref = this.dialog.open<SituReelStartViewPickerDialogComponent, SituReelStartViewPickerData, SituReelStartViewPickerResult>(
      SituReelStartViewPickerDialogComponent,
      {
        data: {
          artifactId: value.artifactId,
          currentStartViewQuery: value.startViewQuery
        },
        width: '92vw',
        maxWidth: '1200px',
        panelClass: 'situ-reel-start-view-picker-dialog'
      }
    );
    ref.afterClosed().subscribe(result => {
      if (result === undefined) return; // cancelled
      ctrl.get('startViewQuery')?.setValue(result ?? '');
      ctrl.markAsDirty();
      this.snackBar.open(
        result ? 'Starting view captured.' : 'Starting view cleared.',
        'OK',
        { duration: 1800 }
      );
    });
  }

  openPicker(): void {
    const ref = this.dialog.open<SituReelItemPickerDialogComponent, void, RenderedOutputSummary>(
      SituReelItemPickerDialogComponent,
      {
        width: '92vw',
        maxWidth: '1500px',
        height: '90vh',
        panelClass: 'situ-reel-picker-dialog'
      }
    );
    ref.afterClosed().subscribe(picked => {
      if (picked) {
        this.addItemFromRenderedOutput(picked);
      }
    });
  }

  openProductPicker(): void {
    const ref = this.dialog.open<SituReelProductPickerDialogComponent, void, Product2>(
      SituReelProductPickerDialogComponent,
      {
        width: '92vw',
        maxWidth: '1500px',
        height: '90vh',
        panelClass: 'situ-reel-picker-dialog'
      }
    );
    ref.afterClosed().subscribe(picked => {
      if (picked) {
        this.addItemFromProduct(picked);
      }
    });
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.snackBar.open('Please fill in the required fields.', 'OK', { duration: 2500 });
      return;
    }
    if (this.items.length === 0) {
      this.snackBar.open('Add at least one item before saving.', 'OK', { duration: 2500 });
      return;
    }
    if (!this.defaultArtifactId) {
      this.defaultArtifactId = this.items.at(0).get('artifactId')?.value;
    }

    const value = this.form.value;
    const reel: SituReel = {
      id: this.reelId,
      name: (value.name ?? '').trim(),
      description: (value.description ?? '').trim() || undefined,
      defaultArtifactId: this.defaultArtifactId!,
      items: (value.items as ItemFormValue[]).map((it, idx) => ({
        artifactId: it.artifactId,
        thumbnailArtifactId: it.thumbnailArtifactId || undefined,
        label: (it.label ?? '').trim() || 'Untitled',
        subtitle: (it.subtitle ?? '').trim() || undefined,
        productName: it.productName,
        thumbnailOverrideUrl: it.thumbnailOverrideUrl || undefined,
        startViewQuery: it.startViewQuery || undefined,
        displayOrder: idx
      }))
    };

    this.saving = true;
    this.reelService.upsertReel(reel, this.data.companyId)
      .then(saved => {
        this.snackBar.open('Reel saved.', 'OK', { duration: 2000 });
        this.dialogRef.close(saved);
      })
      .catch(err => {
        const message = err?.message ? `Could not save the reel: ${err.message}` : 'Sorry — could not save the reel.';
        this.snackBar.open(message, 'OK', { duration: 3500 });
      })
      .finally(() => {
        this.saving = false;
      });
  }

  cancel(): void {
    this.dialogRef.close();
  }

  shareUrl(): string | undefined {
    if (!this.reelId) return undefined;
    const base = this.configService.config.situLinkBaseUrl
      ? `https://reel.${this.configService.config.situLinkBaseUrl}`
      : this.configService.config.situPortalBaseUrl;
    return `${base}/${this.reelId}`;
  }

  getThumbUrl(ctrl: AbstractControl): string {
    const v = ctrl.value as ItemFormValue;
    if (v.thumbnailOverrideUrl) return v.thumbnailOverrideUrl;
    const base = this.configService.config.situViewWebApiBaseUrl;
    const id = v.thumbnailArtifactId || v.artifactId;
    if (v.productName === ProductName.Facade) {
      return `${base}/home/GetImage?artifactId=${id}&fileName=facade.jpg&width=256`;
    }
    if (v.productName === ProductName.ProductPreview) {
      return `${base}/home/GetImage?artifactId=${id}&fileName=preview.webp`;
    }
    return `${base}/home/GetThumb?artifactId=${id}`;
  }

  productLabel(productName?: ProductName): string {
    if (productName === undefined || productName === null) return 'Other';
    return this.productLabels[productName] ?? 'Other';
  }

  onThumbError(ctrl: AbstractControl): void {
    const id = (ctrl.value as ItemFormValue)?.artifactId;
    if (id) this.thumbnailFailed[id] = true;
  }

  isThumbFailed(ctrl: AbstractControl): boolean {
    const id = (ctrl.value as ItemFormValue)?.artifactId;
    return !!id && !!this.thumbnailFailed[id];
  }

  getFallbackIcon(ctrl: AbstractControl): string {
    const productName = (ctrl.value as ItemFormValue)?.productName;
    switch (productName) {
      case ProductName.Facade: return 'home';
      case ProductName.Plan: return 'map';
      case ProductName.Render: return 'image';
      case ProductName.ProductPreview: return 'view_in_ar';
      default: return 'panorama';
    }
  }

  trackByIndex(index: number): number {
    return index;
  }

  private addItemFromProduct(product: Product2): void {
    const artifactId = product.previewArtifactId;
    if (!artifactId) {
      this.snackBar.open('That product has no preview artifact and can’t be added.', 'OK', { duration: 2500 });
      return;
    }
    const item: SituReelItem = {
      artifactId,
      label: product.name?.trim() || product.sku?.trim() || 'Product',
      subtitle: product.sku?.trim() || product.brand?.name?.trim() || undefined,
      productName: ProductName.ProductPreview,
      displayOrder: this.items.length
    };
    this.items.push(this.makeItemGroup(item, this.items.length));
    if (!this.defaultArtifactId) {
      this.defaultArtifactId = item.artifactId;
    }
  }

  private addItemFromRenderedOutput(renderedOutput: RenderedOutputSummary): void {
    const item: SituReelItem = {
      artifactId: renderedOutput.id || '',
      thumbnailArtifactId: renderedOutput.artifactId && renderedOutput.artifactId !== renderedOutput.id
        ? renderedOutput.artifactId
        : undefined,
      label: this.deriveLabel(renderedOutput),
      subtitle: this.deriveSubtitle(renderedOutput),
      productName: renderedOutput.productName,
      displayOrder: this.items.length
    };
    if (!item.artifactId) {
      this.snackBar.open('That render is missing an id and can’t be added.', 'OK', { duration: 2500 });
      return;
    }
    this.items.push(this.makeItemGroup(item, this.items.length));
    if (!this.defaultArtifactId) {
      this.defaultArtifactId = item.artifactId;
    }
  }

  private deriveLabel(ro: RenderedOutputSummary): string {
    const home = ro.homeConfig?.home;
    if (home) return home;
    return this.productLabel(ro.productName);
  }

  private deriveSubtitle(ro: RenderedOutputSummary): string | undefined {
    return ro.homeConfig?.masterFile || undefined;
  }

  private makeItemGroup(item: SituReelItem, index: number): FormGroup {
    return this.fb.group({
      artifactId: [item.artifactId, [Validators.required]],
      thumbnailArtifactId: [item.thumbnailArtifactId ?? null],
      label: [item.label, [Validators.required, Validators.maxLength(60)]],
      subtitle: [item.subtitle ?? '', [Validators.maxLength(80)]],
      productName: [item.productName ?? null],
      thumbnailOverrideUrl: [item.thumbnailOverrideUrl ?? null],
      startViewQuery: [item.startViewQuery ?? null],
      displayOrder: [index]
    });
  }

  private swap(a: number, b: number): void {
    const ctrlA = this.items.at(a);
    const ctrlB = this.items.at(b);
    const valA = ctrlA.value;
    ctrlA.setValue(ctrlB.value);
    ctrlB.setValue(valA);
    this.renumber();
  }

  private renumber(): void {
    this.items.controls.forEach((ctrl, idx) => {
      ctrl.get('displayOrder')?.setValue(idx);
    });
  }
}
